import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { REGISTRATIONS_QUERY_KEY } from '../constants';
import { REGISTRATION_FIELDS } from '../registrationFields.config';
import type { TRegistrationListParams, TRegistrationScopeFilters } from '../types';

// Registration data belongs to the signed-in organization and user; no cache entry is reused across either.
export type TRegistrationScope = { organizationId: string; userId: string };

export const getRegistrationListsQueryKey = ({ organizationId, userId }: TRegistrationScope) =>
  [REGISTRATIONS_QUERY_KEY, organizationId, userId, 'list'] as const;

export const getRegistrationListQueryKey = (scope: TRegistrationScope, params: TRegistrationListParams) =>
  [...getRegistrationListsQueryKey(scope), params] as const;

export const getRegistrationExpirationsQueryKey = ({ organizationId, userId }: TRegistrationScope) =>
  [REGISTRATIONS_QUERY_KEY, organizationId, userId, 'expirations'] as const;

export const getRegistrationDetailsQueryKey = ({ organizationId, userId }: TRegistrationScope, folderId: string) =>
  [REGISTRATIONS_QUERY_KEY, organizationId, userId, 'folder', folderId] as const;

export const getRegistrationSummaryQueryKey = ({ organizationId, userId }: TRegistrationScope) =>
  [REGISTRATIONS_QUERY_KEY, organizationId, userId, 'summary'] as const;

export const isInRegistrationScope = (queryKey: QueryKey, { organizationId, userId }: TRegistrationScope): boolean =>
  queryKey[0] === REGISTRATIONS_QUERY_KEY && queryKey[1] === organizationId && queryKey[2] === userId;

// Lock and permission changes must refresh the open registration form's editability immediately.
// Other folder changes are picked up by the dashboard's normal refetch on mount.
export const invalidateRegistrationQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: [REGISTRATIONS_QUERY_KEY] });

const SEARCHABLE_METADATA_KEYS = REGISTRATION_FIELDS.filter(({ searchable }) => searchable).map(({ key }) => key);

export const toRegistrationScopeRequest = ({ countryIds, productIds }: TRegistrationScopeFilters) => ({
  ...(countryIds.length ? { countryIds: countryIds.join(',') } : {}),
  ...(productIds.length ? { productIds: productIds.join(',') } : {}),
});

// The API pages from zero and takes comma-joined array filters. Empty filters are omitted.
export const toRegistrationListRequest = ({
  page,
  pageSize,
  search,
  countryIds,
  productIds,
  status,
  validity,
  validUntilFrom,
  validUntilTo,
}: TRegistrationListParams): Record<string, string | number> => {
  const request: Record<string, string | number> = {
    page: page - 1,
    pageSize,
    ...toRegistrationScopeRequest({ countryIds, productIds }),
  };
  if (search) {
    request.search = search;
    if (SEARCHABLE_METADATA_KEYS.length) request.searchableMetadataKeys = SEARCHABLE_METADATA_KEYS.join(',');
  }
  if (status.length) request.status = status.join(',');
  if (validity) request.validity = validity;
  if (validUntilFrom) request.validUntilFrom = validUntilFrom;
  if (validUntilTo) request.validUntilTo = validUntilTo;
  return request;
};
